import React, { useEffect, useMemo, useRef, useState } from "react";
import { Utils } from "helpers/utils";
import Loader from "components/Loader";
import LoadingIconWithText from "components/LoadingIconWithText";
import MasterPage1Column from "pages/MasterPage1Column";
import TextBox from "components/TextBox";
import SelectDropdown from "components/SelectDropdown";
import SelectDropdownEmp from "components/SelectDropdownEmp";
import SelectDropdownTaskStatus from "components/SelectDropdownTaskStatus";
import { useRedux } from "hooks";
import { createSelector } from "reselect";
import { debounce } from "lodash";
import { TaskStatus, WpSliceData } from "../../../redux/workplace/wpTypes";
import { loadDllEmployee } from "../../../redux/workplace/wpActions";
import { WpTaskForEditItem } from "types";
import { searchTaskForEditApi, batchUpdateTaskApi, searchDllValueUserApi, BatchEditField } from "api/wpapi";

const BATCH_FIELD_OPTIONS = [
  { value: BatchEditField.TaskStatus.toString(), label: "สถานะงาน" },
  { value: BatchEditField.Responder.toString(), label: "เจ้าของงาน" },
  { value: BatchEditField.Assigner.toString(), label: "ผู้มอบหมาย" },
  { value: BatchEditField.Requester.toString(), label: "ผู้ขอ" },
];

const DEFAULT_STATUS_VALUES = [TaskStatus.ToDo, TaskStatus.OnGoing, TaskStatus.Hold].map(x => x.toString());

interface MainComponentProps {
  onTaskCountChanged(taskCount: number): void;
}

const MainComponent = ({ onTaskCountChanged }: MainComponentProps) => {
  const { dispatch, useAppSelector } = useRedux();
  const storeData = createSelector(
    (state: any) => state.WpSlice,
    (wp: WpSliceData) => ({ dllEmployee: wp.dllEmployee })
  );
  const { dllEmployee } = useAppSelector(storeData);

  useEffect(() => {
    if (dllEmployee == null) {
      dispatch(loadDllEmployee());
    }
  }, []);

  // dllEmployee only has active employees - a resigned person can't be found by typing their name in
  // it, so a type-to-search merges in inactive/resigned hits too (flagged in the label) for the 3
  // responder/assigner/requester filter pickers below
  const [employeeExtra, setEmployeeExtra] = useState<any[]>([]);
  const employeeOptions = useMemo(() => {
    const mergedMap = new Map((dllEmployee ?? []).map((item: any) => [item.value, item]));
    employeeExtra.forEach(item => mergedMap.set(item.value, { ...mergedMap.get(item.value), ...item }));
    return Array.from(mergedMap.values());
  }, [dllEmployee, employeeExtra]);

  const onEmployeeSearchInputChanged = useMemo(() => debounce((inputValue: string, actionMeta?: any) => {
    if (actionMeta && actionMeta.action !== "input-change") return;
    const kw = (inputValue ?? "").trim();
    if (kw.length < 2) return;
    searchDllValueUserApi(kw).then((rs: any[]) => {
      setEmployeeExtra(prev => {
        const mergedMap = new Map(prev.map(item => [item.value, item]));
        (rs ?? []).forEach(item => {
          const label = item.isActive === false ? `${item.label} (ไม่ได้ใช้งาน)` : item.label;
          mergedMap.set(item.value, { value: item.value, label, pictureUrl: item.pictureUrl });
        });
        return Array.from(mergedMap.values());
      });
    }).catch(e => console.error(e));
  }, 400), []);

  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [data, setData] = useState<WpTaskForEditItem[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [keyword, setKeyword] = useState<string>("");
  const [selResponder, setSelResponder] = useState<any[]>([]);
  const [selAssigner, setSelAssigner] = useState<any[]>([]);
  const [selRequester, setSelRequester] = useState<any[]>([]);
  const statusList = Utils.GetTaskStatusForShowInSelect();
  const [selStatus, setSelStatus] = useState<any[]>(() => statusList.filter(x => DEFAULT_STATUS_VALUES.includes(x.value)));
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    onTaskCountChanged(totalCount);
  }, [totalCount]);

  const buildFilterArgs = () => ({
    keyword: keyword || null,
    responderIds: selResponder.length > 0 ? selResponder.map(x => Utils.ToNumber(x.value, 0) ?? 0) : null,
    assignerIds: selAssigner.length > 0 ? selAssigner.map(x => Utils.ToNumber(x.value, 0) ?? 0) : null,
    requesterIds: selRequester.length > 0 ? selRequester.map(x => Utils.ToNumber(x.value, 0) ?? 0) : null,
    taskStatuses: selStatus.length > 0 ? selStatus.map(x => Utils.GetTaskStatusFromVal(Utils.ToNumber(x.value, 0))).filter((x): x is TaskStatus => x != null) : null,
  });

  const hasAnyFilter = () => {
    const f = buildFilterArgs();
    return (f.keyword ?? "").trim() !== "" || (f.responderIds?.length ?? 0) > 0 || (f.assignerIds?.length ?? 0) > 0 || (f.requesterIds?.length ?? 0) > 0 || (f.taskStatuses?.length ?? 0) > 0;
  }

  const fnSearch = async () => {
    if (!hasAnyFilter()) {
      alert("กรุณาระบุเงื่อนไขค้นหาอย่างน้อย 1 อย่าง");
      return;
    }
    setLoading(true);
    setSelectedIds(new Set());
    setHasSearched(true);
    try {
      const f = buildFilterArgs();
      const rs = await searchTaskForEditApi(f.keyword, f.responderIds, f.assignerIds, f.requesterIds, f.taskStatuses, 0);
      setData(rs?.items ?? []);
      setTotalCount(rs?.totalCount ?? 0);
    } catch (e) {
      Utils.HandleException(e, true);
    }
    setLoading(false);
  }

  const fnLoadMore = async () => {
    if (loading || loadingMore || data.length >= totalCount) return;
    setLoadingMore(true);
    try {
      const f = buildFilterArgs();
      const rs = await searchTaskForEditApi(f.keyword, f.responderIds, f.assignerIds, f.requesterIds, f.taskStatuses, data.length);
      setData(prev => [...prev, ...(rs?.items ?? [])]);
      setTotalCount(rs?.totalCount ?? totalCount);
    } catch (e) {
      Utils.HandleException(e, true);
    }
    setLoadingMore(false);
  }

  const TABLE_SCROLL_BOTTOM_THRESHOLD = 60;
  const refTableScroll = useRef<HTMLDivElement>(null);
  const onScrollTryLoadMore = () => {
    try {
      if (loading || loadingMore || data.length >= totalCount) return;
      const el = refTableScroll.current;
      if (!el) return;
      const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distanceToBottom > TABLE_SCROLL_BOTTOM_THRESHOLD) return;
      fnLoadMore();
    } catch (e) {
      Utils.HandleException(e, false);
    }
  }

  type SortKey = "taskName" | "taskStatus" | "createDate" | "assignerName" | "requestName" | "respondName" | "cusTags" | "csName";
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const onSortClick = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortedData = useMemo(() => {
    if (!sortKey) return data;
    const dir = sortDir === "asc" ? 1 : -1;
    const getValue = (tk: WpTaskForEditItem): string | number => {
      switch (sortKey) {
        case "taskStatus": return tk.taskStatus ?? -1;
        case "createDate": return tk.createDate ? new Date(tk.createDate).getTime() : 0;
        default: return (tk[sortKey] ?? "").toString().toLowerCase();
      }
    };
    return [...data].sort((a, b) => {
      const va = getValue(a), vb = getValue(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [data, sortKey, sortDir]);

  const renderSortTh = (key: SortKey, label: string) => (
    <th className="hand" onClick={() => onSortClick(key)} style={{ cursor: "pointer", whiteSpace: "nowrap", userSelect: "none" }}>
      {label}{sortKey === key && (sortDir === "asc" ? " ▲" : " ▼")}
    </th>
  );

  const toggleSelectAll = () => {
    if (selectedIds.size >= data.length && data.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(data.map(x => x.taskId ?? "")));
    }
  }

  const toggleSelectOne = (taskId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId); else next.add(taskId);
      return next;
    });
  }

  const CS_OPTION_VALUE = "__CS__";

  const [batchField, setBatchField] = useState<string | null>(null);
  const [batchStatusValue, setBatchStatusValue] = useState<TaskStatus | null>(null);
  const [batchUserValue, setBatchUserValue] = useState<number | null>(null);
  const [batchToCS, setBatchToCS] = useState<boolean>(false);
  const [applying, setApplying] = useState<boolean>(false);

  const batchFieldNum = Utils.ToNumber(batchField, null);
  const batchValueLabel =
    batchFieldNum === BatchEditField.TaskStatus ? Utils.GetTaskStatusText(batchStatusValue)
      : batchToCS ? "CS ของลูกค้าแต่ละงาน"
        : (dllEmployee ?? []).find((x: any) => x.value == batchUserValue)?.label ?? null;
  const canApply = selectedIds.size > 0 && batchFieldNum != null && (
    batchFieldNum === BatchEditField.TaskStatus ? batchStatusValue != null
      : batchFieldNum === BatchEditField.Responder ? (batchToCS || batchUserValue != null)
        : batchUserValue != null
  );

  const applyResultsAndRefresh = async (allResults: any[]) => {
    const successCount = allResults.filter((x: any) => x.success).length;
    const failed = allResults.filter((x: any) => !x.success);

    setSelectedIds(new Set(failed.map((x: any) => x.postIdTop)));

    if (failed.length <= 0) {
      alert(`เปลี่ยนสำเร็จ ${successCount} งาน`);
    } else {
      alert(`เปลี่ยนสำเร็จ ${successCount} งาน, ไม่สำเร็จ ${failed.length} งาน:\n` + failed.map((x: any) => `- ${x.postIdTop}: ${x.errorMessage}`).join("\n"));
    }

    await fnSearch();
  }

  // "Change to CS" doesn't apply one value to every selected task - each task gets its OWN CUS tag's
  // CS as its new owner, so tasks are grouped by resolved CS user first and one batch call is made
  // per group, with a preview of how many tasks go to each CS before confirming.
  const fnApplyToCS = async () => {
    const selectedTasks = sortedData.filter(tk => selectedIds.has(tk.taskId ?? ""));
    const withCs = selectedTasks.filter(tk => tk.csUserId != null);
    const withoutCsCount = selectedTasks.length - withCs.length;

    if (withCs.length <= 0) {
      alert("งานที่เลือกไม่พบ CS ของลูกค้าให้เปลี่ยน");
      return;
    }

    const groups = new Map<number, { csName: string; taskIds: string[] }>();
    withCs.forEach(tk => {
      const id = tk.csUserId as number;
      if (!groups.has(id)) groups.set(id, { csName: tk.csName ?? `#${id}`, taskIds: [] });
      groups.get(id)!.taskIds.push(tk.taskId ?? "");
    });
    const groupList = Array.from(groups.entries());

    const summaryHtml = groupList.map(([, g]) => `${g.csName}: ${g.taskIds.length} งาน`).join("<br/>");
    const skippedHtml = withoutCsCount > 0 ? `<br/><br/>(ไม่พบ CS สำหรับ ${withoutCsCount} งาน จะไม่เปลี่ยน)` : "";

    const rs = await Utils.SweetConfirm(`ต้องการเปลี่ยนเจ้าของงานเป็น CS ของลูกค้าในแต่ละงาน:<br/>${summaryHtml}${skippedHtml}<br/><br/>รวม ${withCs.length} งาน ใช่หรือไม่?`);
    if (!rs.isConfirmed) return;

    setApplying(true);
    try {
      const allResults: any[] = [];
      for (const [csUserId, g] of groupList) {
        const results = await batchUpdateTaskApi(g.taskIds, BatchEditField.Responder, csUserId.toString());
        allResults.push(...(results ?? []));
      }
      await applyResultsAndRefresh(allResults);
    } catch (e) {
      Utils.HandleException(e, true);
    }
    setApplying(false);
  }

  const fnApply = async () => {
    if (selectedIds.size <= 0) {
      alert("กรุณาเลือกงานที่ต้องการเปลี่ยนก่อน");
      return;
    }
    if (batchFieldNum === BatchEditField.Responder && batchToCS) {
      await fnApplyToCS();
      return;
    }
    if (batchFieldNum == null || (batchFieldNum === BatchEditField.TaskStatus ? batchStatusValue == null : batchUserValue == null)) {
      alert("กรุณาเลือกข้อมูลที่ต้องการเปลี่ยน และค่าใหม่ให้ครบถ้วน");
      return;
    }
    const fieldLabel = BATCH_FIELD_OPTIONS.find(x => x.value == batchField)?.label ?? "";
    const rs = await Utils.SweetConfirm(`ต้องการเปลี่ยน ${fieldLabel} เป็น "${batchValueLabel}" สำหรับงานที่เลือก ${selectedIds.size} งาน ใช่หรือไม่?`);
    if (!rs.isConfirmed) return;

    setApplying(true);
    try {
      const value = batchFieldNum === BatchEditField.TaskStatus ? (batchStatusValue as TaskStatus).toString() : (batchUserValue as number).toString();
      const results = await batchUpdateTaskApi(Array.from(selectedIds), batchFieldNum, value);
      await applyResultsAndRefresh(results ?? []);
    } catch (e) {
      Utils.HandleException(e, true);
    }
    setApplying(false);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 150px)" }}>
      <div className="row" style={{ rowGap: "8px", flexShrink: 0 }}>
        <div className="col-12 col-md-4">
          <TextBox placeholder="ชื่องาน" style={{ padding: "5px" }} icon13css="fas fa-search fz-10 me-1" value={keyword} onChange={(e) => setKeyword(e.target.value)} onEnter13={fnSearch} />
        </div>
        <div className="col-12 col-md-4">
          <SelectDropdown isDisabled={false} isMulti={true} list={employeeOptions} selectedValue={selResponder} onValueChanged={(e) => setSelResponder(e ?? [])} onInputChanged={onEmployeeSearchInputChanged} placeholder={"เจ้าของงาน"} />
        </div>
        <div className="col-12 col-md-4">
          <SelectDropdown isDisabled={false} isMulti={true} list={employeeOptions} selectedValue={selAssigner} onValueChanged={(e) => setSelAssigner(e ?? [])} onInputChanged={onEmployeeSearchInputChanged} placeholder={"ผู้มอบหมาย"} />
        </div>
        <div className="col-12 col-md-4">
          <SelectDropdown isDisabled={false} isMulti={true} list={employeeOptions} selectedValue={selRequester} onValueChanged={(e) => setSelRequester(e ?? [])} onInputChanged={onEmployeeSearchInputChanged} placeholder={"ผู้ขอ"} />
        </div>
        <div className="col-12 col-md-4">
          <SelectDropdown isDisabled={false} isMulti={true} list={statusList} selectedValue={selStatus} onValueChanged={(e) => setSelStatus(e ?? [])} placeholder={"Task Status"} />
        </div>
        <div className="col-12 col-md-auto">
          <button className="btn btn-warning w-100" style={{ height: "38px", padding: "0 16px" }} onClick={fnSearch}>ค้นหา</button>
        </div>
      </div>
      <div className="d-flex justify-content-end" style={{ marginTop: "4px", flexShrink: 0 }}>
        <span className="text-muted">
          {hasSearched && (totalCount > 0 ? `แสดง ${data.length} จาก ${totalCount} งาน` : (loading ? "" : "ไม่มีข้อมูล"))}
        </span>
      </div>

      <div style={{ height: "5px", flexShrink: 0 }}></div>
      {loading && (<Loader />)}
      <div ref={refTableScroll} onScroll={onScrollTryLoadMore} className="div-table-fix-header" style={{ flex: "1 1 auto", minHeight: 0 }}>
        <table>
          <thead>
            <tr>
              <th align="center"><input type="checkbox" checked={data.length > 0 && selectedIds.size >= data.length} onChange={toggleSelectAll} /></th>
              {renderSortTh("taskName", "ชื่องาน")}
              {renderSortTh("taskStatus", "สถานะ")}
              {renderSortTh("createDate", "วันที่สร้าง")}
              {renderSortTh("assignerName", "ผู้มอบหมาย")}
              {renderSortTh("requestName", "ผู้ขอ")}
              {renderSortTh("respondName", "เจ้าของงาน")}
              {renderSortTh("cusTags", "ลูกค้า")}
              {renderSortTh("csName", "CS")}
            </tr>
          </thead>
          <tbody>
            {!loading && !hasSearched && (<tr><td align="center" colSpan={9}>ระบุเงื่อนไขค้นหาอย่างน้อย 1 อย่าง แล้วกด "ค้นหา"</td></tr>)}
            {!loading && hasSearched && data.length <= 0 && (<tr><td align="center" colSpan={9}>ไม่มีข้อมูล</td></tr>)}
            {sortedData.map((tk, index) => {
              const taskIcon = Utils.GetTaskStatusIcon(tk.taskStatus ?? null);
              const taskStatusName = Utils.GetTaskStatusText(tk.taskStatus ?? null);
              const hasMultipleCusTags = (tk.cusTagCount ?? 0) > 1;
              return (
                <tr key={tk.taskId ?? index} className="hover">
                  <td align="center"><input type="checkbox" checked={selectedIds.has(tk.taskId ?? "")} onChange={() => toggleSelectOne(tk.taskId ?? "")} /></td>
                  <td>{tk.taskName}</td>
                  <td>{taskIcon} {taskStatusName}</td>
                  <td>{Utils.GetDMYHHMM(tk.createDate)}</td>
                  <td>{tk.assignerName}</td>
                  <td>{tk.requestName}</td>
                  <td>{tk.respondName}</td>
                  <td>{tk.cusTags}</td>
                  <td>
                    {tk.csName}
                    {hasMultipleCusTags && (
                      <span title="งานนี้มีลูกค้าหลายราย แสดง CS ของรายแรกตามลำดับชื่อลูกค้า อาจมี CS มากกว่า 1 คน"> (*)</span>
                    )}
                  </td>
                </tr>
              )
            })}
            {loadingMore && (<tr><td align="center" colSpan={9}><LoadingIconWithText loadingText={"กำลังโหลดเพิ่มเติม..."} /></td></tr>)}
          </tbody>
        </table>
      </div>

      <div className="d-flex align-items-center flex-wrap" style={{ gap: "8px", marginTop: "10px", borderTop: "dashed gray 1px", paddingTop: "10px", flexShrink: 0 }}>
        <span>เปลี่ยนรายการที่เลือกจาก</span>
        <div style={{ minWidth: "160px" }}>
          <SelectDropdown
            isDisabled={false}
            list={BATCH_FIELD_OPTIONS}
            selectedValue={BATCH_FIELD_OPTIONS.find(x => x.value == batchField) ?? null}
            onValueChanged={(val) => { setBatchField(val); setBatchStatusValue(null); setBatchUserValue(null); setBatchToCS(false); }}
            placeholder={"เลือกข้อมูล"}
          />
        </div>
        <span>เป็น</span>
        <div style={{ minWidth: "200px" }}>
          {batchFieldNum === BatchEditField.TaskStatus ? (
            <SelectDropdownTaskStatus isDisabled={false} selectedStatus={batchStatusValue} onStatusChanged={setBatchStatusValue} />
          ) : batchFieldNum === BatchEditField.Responder ? (
            <SelectDropdown
              isDisabled={false}
              list={[{ value: CS_OPTION_VALUE, label: "Change to CS" }, ...employeeOptions]}
              selectedValue={batchToCS ? { value: CS_OPTION_VALUE, label: "Change to CS" } : (employeeOptions.find((x: any) => x.value == batchUserValue) ?? null)}
              onValueChanged={(val) => {
                if (val === CS_OPTION_VALUE) { setBatchToCS(true); setBatchUserValue(null); }
                else { setBatchToCS(false); setBatchUserValue(Utils.ToNumber(val, null)); }
              }}
            />
          ) : (
            <SelectDropdownEmp isDisabled={batchFieldNum == null} selectedUserId={batchUserValue} onUserChanged={setBatchUserValue} />
          )}
        </div>
        <button className="btn btn-warning" disabled={!canApply || applying} onClick={fnApply}>
          {applying ? "กำลังเปลี่ยน..." : "Apply"}
        </button>
        <span className="text-muted">เลือกไว้ {selectedIds.size} งาน</span>
      </div>
    </div>
  );
}

const DataSettingTaskBatchEdit = () => {
  const onSetEditCol2Key = (key: any) => { }
  const [taskCount, setTaskCount] = useState<number>(0);

  return (
    <MasterPage1Column
      titleText={"แก้ข้อมูลงาน" + (taskCount <= 0 ? "" : (` ${taskCount} งาน`))}
      titleIcon="fas fa-tasks"
      onSetEditCol2Key={onSetEditCol2Key}
      componentCol1={<MainComponent onTaskCountChanged={setTaskCount} />}
    ></MasterPage1Column>
  );
}

export default DataSettingTaskBatchEdit;
